/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchRecords, postFormData } from "../utils/AppUtil";
import { ErrorMessage } from "../utils/ErrorMessage";
import { SpinnerLoading } from "../utils/SpinnerLoading";
import { AnyARecord } from "dns";

type SubscriberType = {
    subscriberId: string,
    email: string,
    newsletterInd: boolean,
    dailyFuneralAlertInd: boolean,
    yahrzeits: [
        {
            id: string,
            firstName: string,
            lastName: string,
            deleted: boolean
        }
    ],
    birthdays: [
        {
            id: string,
            firstName: string,
            lastName: string,
            deleted: boolean
        }
    ],
    stones: [
        {
            id: string,
            firstName: string,
            lastName: string,
            deleted: boolean
        }
    ]
}

export const Subscriptions: React.FC = () => {

    type SubscriberParamsType = { subscriberId: string };
    const params: SubscriberParamsType = useParams();
    const [endpoint, setEndpoint] = useState(`subscriber/subscription?subscriberId=${params.subscriberId}`);

    const subscriberEmail = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [httpError, setHttpError] = useState<any>("");

    const yahrzeitsJSX: React.ReactElement[] = [];
    const StonesJSX: React.ReactElement[] = [];
    const birthdaysJSX: React.ReactElement[] = [];


    const formData: SubscriberType = {
        subscriberId: `${params.subscriberId}`,
        email: "",
        newsletterInd: false,
        dailyFuneralAlertInd: false,
        yahrzeits: [
            {
                id: "",
                firstName: "",
                lastName: "",
                deleted: false
            }
        ],
        birthdays: [
            {
                id: "",
                firstName: "",
                lastName: "",
                deleted: false
            }
        ],
        stones: [
            {
                id: "",
                firstName: "",
                lastName: "",
                deleted: false
            }
        ] 
    }

    const [formState, setFormState] = useState<SubscriberType>(formData);

    const processData = (responseJson: SubscriberType) => {        
        setIsLoading(true);
        setFormState(responseJson);
        setIsLoading(false);
    }

    useEffect(() => {
        fetchRecords(endpoint, processData)
        .catch((error) => {
            setIsLoading(false);
            setHttpError(error.message);
        });
    }, [endpoint]);

    const handleClick = (e: React.FormEvent<HTMLElement>) => {
        e.preventDefault();
        setEndpoint(`subscriber/subscription?subscriberEmail=${subscriberEmail.current?.value}`)
    }



    const handleFormChange = (e: React.ChangeEvent<any>) => {
        
        const form = {...formState};

        
        // options.filter(opt => !opt.assigned).map(opt => someNewObject)  // posible solution from Gavin
        
        if (e.target.type === "checkbox") {

            if (e.target.name === "newsletterInd") {
                form.newsletterInd = e.target.checked;
            }

            if (e.target.name === "dailyFuneralAlertInd") {
                form.dailyFuneralAlertInd = e.target.checked;
            }

            if (e.target.name === "yahrzeits") {
                
                // const i: any = form.yahrzeits.findIndex((r) => {r.id === e.target.id});
                // form.yahrzeits[i].deleted = e.target.checked;

                form.yahrzeits.forEach((element, index) => {
                    if (element.id === e.target.id) {
                        form.yahrzeits[index].deleted = !e.target.checked;
                    }
                });
            }
            if (e.target.name === "stones") {

                form.stones.forEach((element, index) => {
                    if (element.id === e.target.id) {
                        form.stones[index].deleted = !e.target.checked;
                    }
                });
            }
            if (e.target.name === "birthdays") {

                form.birthdays.forEach((element, index) => {
                    if (element.id === e.target.id) {
                        form.birthdays[index].deleted = !e.target.checked;
                    }
                });
            }

            setFormState(form);
        }
    };

    const handlePost = async (e: React.FormEvent<HTMLElement>) => {
        e.preventDefault();
        postFormData(
            `subscriber/${formState.subscriberId}/subscription`,
            formState,
            (response?: any) => {

            },
            (response: any) => { // error
                console.log("Form error" + response);
            }
        );
        alert("Subscriptions Updated");
        window.location.reload();
    }

    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    if(httpError) {
        return(
            <ErrorMessage error={httpError} />
        )
    }

    return (
        <div className="jc-grid mx-auto" style={{maxWidth: "1028px"}}>
            <p className="jc-txt-heading jc-txt-dark fs-3 mb-4">Subscriptions</p>
        
            <form id="subscriptionsForm">
                <div className="d-flex flex-column gap-1 pb-2">

                    <label htmlFor="subscriberEmail" className="jc-txt-body jc-txt-dark fs-6">Enter your email address to search for Subscriptions:</label>
                    <div className="d-flex gap-3 mb-4">
                        <input ref={subscriberEmail} id="subscriberEmail" type="email" className="form-control" placeholder="Your valid email" />
                        <button type="button" onClick={handleClick} className="btn-submit btn btn-dark jc-txt-body jc-txt-light">Search</button>
                    </div>

                        <div className="d-flex flex-column">

                            <p className="jc-txt-body jc-txt-dark fw-bold fs-3 mb-4">{formState.email}</p> 

                            <p className="jc-txt-body fs-4">Subscriptions:</p>
                
                            <div className="d-flex form-check p-0">
                                <input id="newsletterInd" name="newsletterInd" type="checkbox" role="check" checked={formState.newsletterInd} onChange={handleFormChange} className="form-check-input ms-0 mt-2 me-2" />
                                <label className="form-label fs-6 nowrap mt-1">Newsletter</label>
                            </div>
                
                            <div className="d-flex form-check p-0">
                                <input id="dailyFuneralAlertInd" name="dailyFuneralAlertInd" type="checkbox" role="check" checked={formState.dailyFuneralAlertInd} onChange={handleFormChange} className="form-check-input ms-0 mt-2 me-2" />
                                <label className="form-label fs-6 nowrap mt-1">Upcoming Funeral Alerts</label>
                            </div>

                            
                            {formState.yahrzeits[0] && <p className="jc-txt-body fs-4 mt-5 mb-2">Yahrzeit Reminders:</p>}
                            {formState.yahrzeits.map((r) => {
                                return (
                                    <div className="">
                                        <input id={r.id} name="yahrzeits" type="checkbox" role="check" checked={!r.deleted} onChange={handleFormChange} className="form-check-input ms-0 mt-2 me-2" />
                                        <label className="form-label fs-6 nowrap mt-1">{r.firstName} {r.lastName}</label>
                                    </div>
                                )
                            })}
                
                            {formState.stones[0] && <p className="jc-txt-body fs-4 mt-5 mb-2">Stone Reminders:</p>}
                            {formState.stones.map((r) => {
                                return (
                                    <div className="">
                                        <input id={r.id} name="stones" type="checkbox" role="check" checked={!r.deleted} onChange={handleFormChange} className="form-check-input ms-0 mt-2 me-2" />
                                        <label className="form-label fs-6 nowrap mt-1">{r.firstName} {r.lastName}</label>
                                    </div>
                                )
                            })}
                            
                            {formState.birthdays[0] && <p className="jc-txt-body fs-4 mt-5 mb-2">Birthday Reminders:</p>}
                            {formState.birthdays.map((r) => {
                                return (
                                    <div className="">
                                        <input id={r.id} name="birthdays" type="checkbox" role="check" checked={!r.deleted} onChange={handleFormChange} className="form-check-input ms-0 mt-2 me-2" />
                                        <label className="form-label fs-6 nowrap mt-1">{r.firstName} {r.lastName}</label>
                                    </div>
                                )
                            })}
                            
                
                            <div className="mt-3">
                                <button type="button" onClick={handlePost} className="btn btn-dark">Update</button>
                            </div>
                
                        </div>

                </div>
            </form>

        </div>
    );
}